import React from "react";

/**
 * guide_actor — Anivaze's cute guide mascot ("Vaze").
 * A friendly round character that NARRATES the lesson: its mouth moves while the
 * voice is talking, and it reacts with expressions (explaining, thinking,
 * curious, surprised, celebrating). Pure function of `t`.
 */

export interface GuideActorProps {
  x?: number;
  y?: number;
  scale?: number;
  emotion?: string; // explaining | thinking | curious | surprised | celebrating | confident | caution | neutral
  pointDirection?: "left" | "right" | "up" | "none";
  opacity?: number;
  accentColor?: string;
  /** Seconds since the scene started — drives idle bob, blink, talking, sparkles. */
  t?: number;
  /** When true, the mouth animates as if speaking (sync to narration playback). */
  talking?: boolean;
}

export const GuideActor: React.FC<GuideActorProps> = ({
  x = 712,
  y = 402,
  scale: s = 1,
  emotion = "neutral",
  pointDirection = "none",
  opacity = 1,
  accentColor = "#8b5cf6",
  t = 0,
  talking = false,
}) => {
  const uid = "vaze";
  const bob = Math.sin(t * 2.2) * 2.2;
  const lean = Math.sin(t * 1.3) * 2;
  const blink = (t % 3.2) > 3.0 ? 0.12 : 1;
  const antenna = Math.sin(t * 3) * 6;
  const happy = emotion === "celebrate" || emotion === "celebrating" || emotion === "confident";
  const thinking = emotion === "thinking";
  const surprised = emotion === "surprised";
  const lookUp = thinking || emotion === "curious";

  // Arms
  const armL = pointDirection === "left" ? { x: -46, y: -54 }
    : happy ? { x: -40, y: -82 } : { x: -34, y: -40 };
  const armR = pointDirection === "right" ? { x: 46, y: -54 }
    : pointDirection === "up" ? { x: 20, y: -96 }
    : thinking ? { x: 18, y: -52 }                 // hand near chin, pondering
    : emotion === "explaining" ? { x: 40, y: -62 }
    : happy ? { x: 40, y: -82 } : { x: 34, y: -40 };

  // Mouth — talking overrides the resting expression.
  const talkOpen = 1.4 + Math.abs(Math.sin(t * 12)) * 4.6;
  const mouth = talking ? (
    <ellipse cx={0} cy={-43} rx={5.5} ry={talkOpen} fill="#7f1d1d" />
  ) : surprised ? (
    <ellipse cx={0} cy={-43} rx={5.5} ry={7} fill="#7f1d1d" />
  ) : happy ? (
    <path d="M-9,-46 Q0,-34 9,-46 Q0,-42 -9,-46 Z" fill="#7f1d1d" />
  ) : emotion === "explaining" ? (
    <ellipse cx={0} cy={-44} rx={5} ry={5.5} fill="#7f1d1d" />
  ) : emotion === "caution" ? (
    <rect x={-6} y={-45} width={12} height={2.6} rx={1.3} fill="#7f1d1d" />
  ) : (
    <path d="M-8,-46 Q0,-39 8,-46" fill="none" stroke="#7f1d1d" strokeWidth={2.4} strokeLinecap="round" />
  );

  const eyeW = surprised ? 8.5 : 7;
  const eyeRy = (surprised ? 9 : 7) * blink;
  const pupilCy = lookUp ? -56 : surprised ? -54 : -53;

  return (
    <g opacity={opacity} transform={`translate(${x}, ${y}) scale(${s})`}>
      <defs>
        <radialGradient id={`${uid}-body`} cx="38%" cy="30%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.5" />
          <stop offset="45%" stopColor={accentColor} />
          <stop offset="100%" stopColor="#4c1d95" />
        </radialGradient>
      </defs>

      <g transform={`translate(${lean}, ${bob})`}>
        <ellipse cx={0} cy={2} rx={30} ry={6} fill="#000" opacity={0.18} />

        {/* Antenna */}
        <g transform={`rotate(${antenna} 0 -84)`}>
          <line x1={0} y1={-84} x2={0} y2={-104} stroke="#6d28d9" strokeWidth={3} strokeLinecap="round" />
          <circle cx={0} cy={-107} r={5} fill="#fcd34d" stroke="#f59e0b" strokeWidth={1} />
        </g>

        {/* Feet */}
        <ellipse cx={-13} cy={-2} rx={9} ry={5} fill="#4c1d95" />
        <ellipse cx={13} cy={-2} rx={9} ry={5} fill="#4c1d95" />

        {/* Arms */}
        <path d={`M-26,-50 Q${armL.x - 4},${armL.y + 6} ${armL.x},${armL.y}`} fill="none" stroke={accentColor} strokeWidth={9} strokeLinecap="round" />
        <path d={`M26,-50 Q${armR.x + 4},${armR.y + 6} ${armR.x},${armR.y}`} fill="none" stroke={accentColor} strokeWidth={9} strokeLinecap="round" />
        <circle cx={armL.x} cy={armL.y} r={6.5} fill="#a78bfa" />
        <circle cx={armR.x} cy={armR.y} r={6.5} fill="#a78bfa" />

        {/* Body */}
        <path d="M0,-12 C-30,-12 -34,-46 -30,-66 C-26,-86 26,-86 30,-66 C34,-46 30,-12 0,-12 Z" fill={`url(#${uid}-body)`} stroke="#4c1d95" strokeWidth={2} />
        <ellipse cx={-9} cy={-58} rx={9} ry={13} fill="#fff" opacity={0.18} />

        {/* Cheeks */}
        <circle cx={-15} cy={-40} r={5} fill="#fb7185" opacity={0.55} />
        <circle cx={15} cy={-40} r={5} fill="#fb7185" opacity={0.55} />

        {/* Eyes */}
        <ellipse cx={-9} cy={-54} rx={eyeW} ry={eyeRy} fill="#fff" />
        <ellipse cx={9} cy={-54} rx={eyeW} ry={eyeRy} fill="#fff" />
        {blink > 0.5 && (
          <g>
            <circle cx={-8} cy={pupilCy} r={3.4} fill="#1e1b4b" />
            <circle cx={10} cy={pupilCy} r={3.4} fill="#1e1b4b" />
            <circle cx={-9.5} cy={pupilCy - 2} r={1.3} fill="#fff" />
            <circle cx={8.5} cy={pupilCy - 2} r={1.3} fill="#fff" />
          </g>
        )}
        {(thinking || emotion === "curious") && <path d="M4,-64 Q10,-67 15,-63" fill="none" stroke="#4c1d95" strokeWidth={2} strokeLinecap="round" />}

        {mouth}

        {/* Thought / speech bubble */}
        {(emotion === "explaining" || emotion === "curious" || emotion === "caution" || thinking || surprised) && (
          <g>
            <circle cx={26} cy={-86} r={11} fill="#fff" stroke={accentColor} strokeWidth={1.5} />
            <circle cx={16} cy={-74} r={3} fill="#fff" stroke={accentColor} strokeWidth={1} />
            <text x={26} y={-81} textAnchor="middle" fill={accentColor} fontSize={12} fontWeight={800} fontFamily="Inter, system-ui, sans-serif">
              {emotion === "caution" || surprised ? "!" : thinking ? "…" : "?"}
            </text>
          </g>
        )}

        {/* Talking ripples near the mouth */}
        {talking && (
          <g opacity={0.5}>
            <path d={`M34,-44 q6,${-3 - Math.abs(Math.sin(t * 12)) * 3} 0,-6`} fill="none" stroke={accentColor} strokeWidth={1.5} strokeLinecap="round" />
            <path d={`M40,-44 q9,${-4 - Math.abs(Math.sin(t * 12 + 1)) * 4} 0,-8`} fill="none" stroke={accentColor} strokeWidth={1.5} strokeLinecap="round" opacity={0.6} />
          </g>
        )}

        {/* Celebrate sparkles */}
        {happy && (
          [{ x: -30, y: -96, p: 0 }, { x: 30, y: -100, p: 1.1 }, { x: -16, y: -108, p: 2.0 }, { x: 18, y: -90, p: 0.6 }].map((sp, i) => {
            const a = (Math.sin(t * 3 + sp.p) + 1) / 2;
            return <g key={i} transform={`translate(${sp.x}, ${sp.y - a * 5})`} opacity={0.3 + a * 0.6}>
              <path d="M0,-4 L1.2,-1.2 L4,0 L1.2,1.2 L0,4 L-1.2,1.2 L-4,0 L-1.2,-1.2 Z" fill={i % 2 ? "#fcd34d" : accentColor} />
            </g>;
          })
        )}
      </g>
    </g>
  );
};
